/**
 * Die Artikelkalkulation des EK-Reiters - Portierung von `ekTableHtml` (`index.html:10307-10325`)
 * und `ekTableRow` (`:10265-10305`). Urspruenglich 23 Spalten, Reihenfolge und Klassen
 * unveraendert; die fuenf Zollspalten stehen in `Zolltarifgruppen.tsx`, die beiden
 * Ergebnisspalten in `Ueberschreibungen.tsx`.
 *
 * ================= Task 6 (2026-08-26): Spalte „Datum" entfernt (23 -> 22 Spalten) =================
 *
 * Das Feld schrieb `bookedDate` (`patchOrderCalcLive('bookedDate', …)`) - dasselbe Bestellfeld, das
 * Task 2 backendseitig entfernt hat (`booked_date`/`delivery_booked` sind keine Spalten von
 * `order_extras` mehr). Jede Eingabe hier warf seither `FeldOhneGruppeFehler` (Toast „Die Bestellung
 * konnte nicht geaendert werden."), ohne dass die Tabelle das erkennen liess - ein echter
 * Regressionsbug, seit Task 2 eingetreten und mit dieser Aenderung behoben. Ersatz ist der neue
 * Reiter „Anlieferung" (Task 4/5). Damit weicht diese Tabelle NEU von der Baseline `drawer-calc` ab -
 * bewusst, siehe Ausnahmeregister `test/visual/ausnahmen.ts` (Task 8).
 *
 * ================= KEINE Abweichung mehr gegenueber dem Bestand (nachgezogen) ====================
 *
 * Zwei vom Task-21-Brief erzwungene Abweichungen sind hier ZURUECKGENOMMEN. Grund: die optische
 * Paritaet zum Bestand ist in diesem Projekt ein PRUEFBARES Kriterium der Frontend-Spec und steht
 * als Global Constraint ueber dem Text einer einzelnen Aufgabe; beides hatte im Bestand kein
 * Gegenstueck und war damit ein Zusatz, den die Woertlichkeitsregel verbietet. Siehe auch
 * `Ueberschreibungen.tsx` (dort dasselbe fuer die Ergebnisspalten).
 *
 * 1. Die Spalte „Gebühren EUR" formatiert wieder mit `EUR`, wie `index.html:10297`
 *    (`EUR.format(r.freightFeesShare)`). Die Baseline `drawer-calc` zeigt an dieser Stelle „0,00 €"
 *    (`span.ek-static-value`, Knoten 124 und 171 des `.drawer-body`-Ausschnitts) - mit `EKF` stand
 *    dort „0,00" und der Vergleich waere rot. Die Briefzeile „`EKF` ist das EK-Zahlenformat" gilt
 *    fuer Stueckpreise; `freightFeesShare` ist ein Rechnungsbetrag, kein Stueckpreis.
 * 2. Die Spalte „Stück" traegt wieder `title={qtySource}` (`:10286`). Das Attribut steht im Bestand,
 *    also steht es hier.
 *
 * Die vom Brief verlangte SICHTBARE FOC-Menge (`span.ek-static-value` mit `title` in der Spalte
 * „FOC") ist damit entfallen - der Bestand hat dort NUR das Eingabefeld (`:10284`, Baseline-Knoten
 * 97-98 bzw. 144-145: `td.ek-num` mit genau EINEM `input`). Der Namenskonflikt, der den Vorgaenger
 * zwang, das `title` der Spalte „Stück" wegzulassen (`getByTitle(/FOC/)` haette zwei Elemente
 * gefunden), loest sich damit von selbst; der zugehoerige Test greift jetzt ueber die
 * Spaltenbeschriftung auf den `value` des FOC-Feldes zu (`ekTab.test.tsx`, `spalteInZeile`).
 *
 * ================= Wo die zugeordnete FOC-Menge steht =================
 *
 * Im `value` des FOC-Eingabefeldes: `r.focInputValue` ist `linkedFocQty`, sobald eine FOC-Zeile
 * zugerechnet wurde und keine eigene Menge eingetragen ist (`landedCost.ts:294-296`). Ein Wert, den
 * keine Textabfrage findet und den ein Bildschirmleser nur beim Betreten des Feldes nennt - ein
 * Befund am Bestand, GEMELDET und nicht repariert.
 */
import { Zolltarifgruppen } from "./Zolltarifgruppen.tsx";
import { Ueberschreibungen } from "./Ueberschreibungen.tsx";
import { Artikelbild } from "../../Artikelbild.tsx";
import { EUR, NUM } from "../../../lib/format.ts";
import { feldZustandClass } from "../../../utils/feldZustand.ts";
import type { CalcEkResult, EkRow } from "../../../lib/landedCost.ts";

/** `esc(s) = String(s ?? '')` (`index.html:4674`). */
const roh = (value: unknown): string => String(value ?? "");

/** `index.html:10269-10272`, woertlich - der Wert eines berechneten Zahlenfeldes. */
export function inputNumber(value: unknown, digits = 4): string {
  const factor = 10 ** digits;
  return String(Math.round((Number(value) || 0) * factor) / factor);
}

/** `index.html:10268`, woertlich - der Platzhalter eines Geldfeldes. */
export function moneyPlaceholder(value: unknown): string {
  return String(Math.round((Number(value) || 0) * 100) / 100);
}

/** `index.html:10273-10275`, woertlich (Fassung des Erstzeichnens, siehe `EkTab.tsx`). */
export function qtySourceLabel(r: EkRow): string {
  return r.qtyPlIsSet
    ? r.linkedFocQty > 0
      ? "PL + FOC"
      : "Packing List"
    : r.focQty > 0
      ? "Bestellung + FOC"
      : "Bestellung";
}

export interface PositionsTabelleProps {
  /** Das lokale `calcEk`-Ergebnis. Wird gelesen, NIE weitergegeben (siehe `EkTab.tsx`). */
  ergebnis: CalcEkResult;
  darfBearbeiten: boolean;
  /** `patchItemCalcLive` (`index.html:9980`) - ein flaches Aenderungsobjekt je Position. */
  onPosition: (i: number, aenderung: Record<string, unknown>) => void;
}

export function PositionsTabelle({ ergebnis: c, darfBearbeiten, onPosition }: PositionsTabelleProps) {
  return (
    <div className="ek-sheet">
      <div className="ek-sheet-head">
        <strong>
          {`Artikelkalkulation · ${NUM.format(c.rows.length)} Position${c.rows.length === 1 ? "" : "en"}`}
        </strong>
        <span>Horizontal und vertikal scrollen · Artikel und Bezeichnung bleiben sichtbar</span>
      </div>
      <div className="ek-table-scroll" tabIndex={0} aria-label="Einkaufspreiskalkulation als Tabelle">
        <table className="ek-table">
          <thead>
            <tr>
              <th>Artikel</th>
              <th>Bezeichnung</th>
              <th className="num">Bestellmenge</th>
              <th className="num">FOC</th>
              <th className="num">Geliefert PL</th>
              <th className="num">Stück</th>
              <th className="num">{`Rechnung ${c.isUsd ? "USD" : "EUR"}`}</th>
              <th className="num">Kurs</th>
              <th className="num">Mischkurs</th>
              <th className="num">{`EK ${c.isUsd ? "USD" : "EUR"}`}</th>
              <th>Zolltarif</th>
              <th className="num">Zoll Gruppe</th>
              <th className="num">Zoll EUR</th>
              <th className="num">Zoll JTL %</th>
              <th className="num">Zoll ber. %</th>
              <th className="num">Transport EUR</th>
              <th className="num">Gebühren EUR</th>
              <th className="num">Werkzeug EUR</th>
              <th className="num">Bestand alt</th>
              <th className="num">EK alt</th>
              <th className="num">EK DE</th>
              <th className="num">EK neu</th>
            </tr>
          </thead>
          <tbody>
            {c.rows.map((r) => (
              <EkZeile key={r.i} zeile={r} darfBearbeiten={darfBearbeiten} onPosition={onPosition} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="ek-sheet-foot">
        <span>Eingaben werden sofort gespeichert und neu berechnet.</span>
        <span>Automatisch verteilte Werte stehen als Platzhalter in den Eingabefeldern.</span>
      </div>
    </div>
  );
}

interface EkZeileProps {
  zeile: EkRow;
  darfBearbeiten: boolean;
  onPosition: (i: number, aenderung: Record<string, unknown>) => void;
}

/** `ekTableRow` (`index.html:10265-10305`). */
function EkZeile({ zeile: r, darfBearbeiten, onPosition }: EkZeileProps) {
  const it = r.it;
  const qtySource = qtySourceLabel(r);
  /**
   * BENANNTE LUECKE, laut statt still, und sie gilt fuer JEDE Eingabe dieser Zeile: `patch` schreibt
   * nicht die Bestellung, sondern eine POSITION - `onPosition` fuehrt ueber
   * `EkTab.tsx#positionPatchen` auf `onAendern(id, { items })`. Und `"items"` steht in KEINER
   * Feldgruppe (`api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG`): eigene Tabelle `order_item_extras`, Weg
   * ist `PATCH /api/orders/:id/items/:jtlPosId`, wofuer es im Frontend noch kein Modul gibt. RIEGEL 2
   * laesst `bestellungAendern` deshalb mit `FeldOhneGruppeFehler` scheitern, BEVOR ueberhaupt etwas
   * gesendet wird: jede Eingabe hier bleibt ohne dauerhafte Wirkung, aber der Fehlschlag ist LAUT
   * (Konsole + Toast in `App.tsx#drawerAendern`).
   */
  const patch = (schluessel: string, wert: unknown): void => onPosition(r.i, { [schluessel]: wert });

  /** `patchEkUnitLive` (`index.html:10001-10011`) - der Stueckpreis schreibt die Rechnungssumme. */
  const ekOriginalSetzen = (wert: string): void =>
    patch("supplierInvoice", wert === "" ? "" : Math.max(0, Number(wert) || 0) * r.qtyEff);

  return (
    <tr data-ek-item-index={r.i}>
      <td className="ek-col-sku">{roh(it.sku) || "—"}</td>
      {/*
       * Auftrag 2, ausdruecklich KEINE eigene Spalte (siehe Kopfkommentar dieser Datei): das
       * Thumbnail sitzt IN dieser vorhandenen Zelle, Bild und Text nebeneinander per Flex - die
       * Spaltenzahl (22) und damit `nth-child(1)`/`nth-child(2)` in `bestand.css` bleiben unberuehrt.
       */}
      <td className="ek-col-name">
        <div className="ek-col-name-inner">
          <Artikelbild kArtikel={it.kArtikel} name={it.name} hatBild={it.hatBild as boolean | undefined} />
          <span className="ek-col-name-text">
            <span className="ek-row-name">{roh(it.name) || `Artikel ${r.i + 1}`}</span>
            <span className="ek-row-source">{qtySource}</span>
          </span>
        </div>
      </td>
      {/* `patchEkOrderedQtyLive` (index.html:9988) - schreibt `qty`, nicht `orderedQty`. */}
      <td className="ek-num">
        <input
          type="number"
          min="0"
          value={inputNumber(r.orderedQty, 2)}
          onChange={(e) => patch("qty", e.currentTarget.value)}
        />
      </td>
      {/* `index.html:10284` - `value` traegt die zugeordnete FOC-Menge; kein zweiter Knoten daneben. */}
      <td className="ek-num">
        <input
          type="number"
          min="0"
          value={roh(r.focInputValue)}
          placeholder="0"
          onChange={(e) => patch("focQty", e.currentTarget.value)}
        />
      </td>
      <td className="ek-num">
        <input
          type="number"
          min="0"
          value={roh(it.qtyPl)}
          placeholder={roh(r.orderedQty || "0")}
          onChange={(e) => patch("qtyPl", e.currentTarget.value)}
        />
      </td>
      {/* `index.html:10286` - `title="${esc(qtySource)}"`, wie im Bestand (siehe Kopfkommentar). */}
      <td className="ek-num ek-computed">
        <input
          type="number"
          min="0"
          value={inputNumber(r.qtyEff, 2)}
          title={qtySource}
          onChange={(e) => patch("qtyEffOverride", e.currentTarget.value)}
        />
      </td>
      <td className="ek-num">
        <input
          type="number"
          min="0"
          step="0.01"
          value={roh(it.supplierInvoice)}
          placeholder={moneyPlaceholder(r.supplierShare)}
          onChange={(e) => patch("supplierInvoice", e.currentTarget.value)}
        />
      </td>
      <td className="ek-num">
        {/*
         * Dieselbe Luecke wie am `patch` oben, aber NICHT aus dem Grund, den der Feldname nahelegt:
         * `"usdRate"` steht zwar auch selbst in `api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG` (es
         * gehoert zur RATE, `order_payment_schedule.usd_rate`) - hier ist es jedoch ein
         * POSITIONS-Schluessel. Und selbst ueber den seit 2026-08-13 tatsaechlich schreibenden
         * `onPositionAendern`-Weg (`EkTab.tsx#positionPatchen`, `api/positionen.ts`) fuehrt kein Weg:
         * `"usdRate"` steht NICHT in `api/positionen.ts#POSITIONS_FELDER`, der Allowlist dieser
         * Route - ein Schreibversuch schiede dort mit `PositionsfeldNichtSchreibbarFehler`. Anders
         * als der Rest dieser Zeile (die MEISTEN anderen Felder hier SPEICHERN inzwischen wirklich,
         * siehe `EkTab.tsx`, "SEIT DEM 2026-08-13 GEHT DIESER WEG WIRKLICH IRGENDWOHIN") ist DIESES
         * Feld auf KEINEM der beiden Wege speicherbar - ohne dauerhafte Wirkung, egal welcher Pfad.
         *
         * NACHBESSERUNG (Auftrag "ZahlungTab-Kurs-Sperre", 2026-09-07): dieselbe Behandlung, die das
         * gleichnamige Feld im Zahlungsreiter hatte (das Feld entfiel dort mit Ticket H, 2026-10-20) - `readOnly`
         * statt eines unmarkierten Feldes, Schloss-Icon per `feldZustandClass({ gesperrt: true })`,
         * `title` mit nutzerverstaendlicher Begruendung statt des internen Fehlernamens.
         */}
        <input
          className={feldZustandClass({ gesperrt: true })}
          type="number"
          min="0"
          step="0.0001"
          value={roh(it.usdRate)}
          placeholder={r.rate ? inputNumber(r.rate) : "1.0850"}
          readOnly
          aria-readonly="true"
          title="Diese Eingabe wird aktuell nicht gespeichert – für den manuellen Dollarkurs gibt es noch keinen Speicherweg."
        />
      </td>
      <td className="ek-num">
        {/*
         * Ebenso: `"mixedUsdRate"` steht in `KEIN_FELDGRUPPEN_WEG` als VERWORFEN (der Mischkurs wird
         * aus `fx_deals` + `fx_allocations` gerechnet, es gibt keine Spalte dafuer) UND fehlt aus
         * demselben Grund in `api/positionen.ts#POSITIONS_FELDER` - auch der neuere Positionsweg
         * fuehrt hier ins Leere.
         *
         * NACHBESSERUNG (Auftrag "ZahlungTab-Kurs-Sperre", 2026-09-07): dieselbe Behandlung wie beim
         * Dollarkurs-Feld oben. Begruendung siehe dort.
         */}
        <input
          className={feldZustandClass({ gesperrt: true })}
          type="number"
          min="0"
          step="0.0001"
          value={roh(it.mixedUsdRate)}
          placeholder="optional"
          readOnly
          aria-readonly="true"
          title="Diese Eingabe wird aktuell nicht gespeichert – der Mischkurs wird automatisch aus den gebundenen Kurssicherungen berechnet."
        />
      </td>
      <td className="ek-num ek-computed">
        <input
          type="number"
          min="0"
          step="0.0001"
          value={inputNumber(r.ekOriginal)}
          onChange={(e) => ekOriginalSetzen(e.currentTarget.value)}
        />
      </td>
      <Zolltarifgruppen zeile={r} onPosition={onPosition} />
      <td className="ek-num">
        <input
          type="number"
          min="0"
          step="0.01"
          value={roh(it.freightInvoice)}
          placeholder={moneyPlaceholder(r.freightShare)}
          onChange={(e) => patch("freightInvoice", e.currentTarget.value)}
        />
      </td>
      {/* `index.html:10297` - `EUR.format`, wie im Bestand (siehe Kopfkommentar). */}
      <td className="ek-num ek-computed">
        <span className="ek-static-value">{EUR.format(r.freightFeesShare)}</span>
      </td>
      <td className="ek-num">
        <input
          type="number"
          min="0"
          step="0.01"
          value={roh(it.toolingCost)}
          placeholder={moneyPlaceholder(r.toolingShare)}
          onChange={(e) => patch("toolingCost", e.currentTarget.value)}
        />
      </td>
      <td className="ek-num">
        <input
          type="number"
          min="0"
          value={roh(it.stockOld)}
          placeholder="0"
          onChange={(e) => patch("stockOld", e.currentTarget.value)}
        />
      </td>
      <td className="ek-num">
        <input
          type="number"
          min="0"
          step="0.0001"
          value={roh(it.ekOld)}
          placeholder="0,0000"
          onChange={(e) => patch("ekOld", e.currentTarget.value)}
        />
      </td>
      <Ueberschreibungen zeile={r} darfBearbeiten={darfBearbeiten} onPosition={onPosition} />
    </tr>
  );
}
